import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import {
  DeliverySummary,
  deliveredShare,
  formatShare,
  noReceiptPhrase,
} from '../../../../../services/message/delivery.service';
import { formatCount } from '../../../../../../utils/sms';
import { DeliveryFilter, receiptsDue } from '../report-shared';

interface Tile {
  filter: DeliveryFilter;
  value: string;
  label: string;
  /** Legend swatch matching the bar segment. */
  swatch: 'ok' | 'bad' | 'wait' | null;
  bad: boolean;
  /** A second line under the label, e.g. what "no receipt yet" is made of. */
  note?: string;
}

/**
 * Where a send's recipients ended up: the delivered share first, a bar of
 * delivered / failed / still in flight, then one tile per state. Shared by
 * the message details page (tiles open the report on that state) and the
 * report (tiles filter the recipient list).
 */
@Component({
  selector: 'message-delivery-summary',
  standalone: true,
  templateUrl: './delivery-summary.component.html',
  styleUrl: './delivery-summary.component.css',
})
export class DeliverySummaryComponent implements OnChanges {
  @Input() summary: DeliverySummary | null = null;
  @Input() state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  /** Message status: an active send is still delivering. */
  @Input() status: string | null | undefined = '';
  /** When the message went out, to tell "in flight" from "no receipt". */
  @Input() sentAt: Date | null = null;
  /** Blacklisted recipients left out of the send, when known. */
  @Input() blacklisted: number | null = null;
  /** 'filter': tiles toggle a filter (report). 'link': tiles open the report. */
  @Input() mode: 'filter' | 'link' = 'filter';
  @Input() active: DeliveryFilter | null = null;

  @Output() pick = new EventEmitter<DeliveryFilter>();

  readonly formatCount = formatCount;

  tiles: Tile[] = [];
  share = '';
  leadLabel = '';
  breakdown = '';

  get total(): number {
    return this.summary?.total ?? 0;
  }

  get failed(): number {
    return this.summary?.failed ?? 0;
  }

  get delivered(): number {
    return this.summary?.delivered ?? 0;
  }

  get inFlight(): number {
    return this.summary?.inFlight ?? 0;
  }

  ngOnChanges(): void {
    const summary = this.summary;
    if (!summary) {
      this.tiles = [];
      return;
    }

    const due = receiptsDue(this.status, this.sentAt);
    // "No receipt yet" covers two cases: still queued to send, and sent with
    // the network yet to report back. The note says which, so a send from
    // hours ago never reads as still waiting in line.
    const waitingLabel = due ? 'No receipt yet' : 'No receipt';
    const queued = summary.queued ?? 0;
    const waitingNote = !summary.inFlight
      ? undefined
      : queued >= summary.inFlight
        ? 'Not sent yet'
        : queued > 0
          ? `${formatCount(queued)} not sent yet`
          : due
            ? 'Sent, awaiting receipt'
            : undefined;
    this.share = formatShare(deliveredShare(summary));
    this.leadLabel =
      summary.inFlight > 0 && due
        ? `delivered so far, of ${formatCount(summary.total)}`
        : `delivered, of ${formatCount(summary.total)}`;

    const parts = [`${formatCount(summary.delivered)} delivered`];
    if (summary.failed) parts.push(`${formatCount(summary.failed)} failed`);
    if (summary.inFlight) parts.push(noReceiptPhrase(summary, due));
    this.breakdown = `${parts.join(', ')}, of ${formatCount(summary.total)} recipients`;

    this.tiles = [
      { filter: 'all', value: formatCount(summary.total), label: 'Recipients', swatch: null, bad: false },
      { filter: 'delivered', value: formatCount(summary.delivered), label: 'Delivered', swatch: 'ok', bad: false },
      {
        filter: 'failed',
        value: formatCount(summary.failed),
        label: 'Failed',
        swatch: 'bad',
        bad: summary.failed > 0,
      },
      {
        filter: 'inflight',
        value: formatCount(summary.inFlight),
        label: waitingLabel,
        swatch: 'wait',
        bad: false,
        note: waitingNote,
      },
    ];
  }

  tileTitle(tile: Tile): string {
    if (this.mode === 'link') return `Open the report on ${tile.label.toLowerCase()}`;
    return tile.filter === 'all' ? 'Show every recipient' : `Show ${tile.label.toLowerCase()} only`;
  }
}
